/**
 * Inline Container Block Style Generation
 *
 * Contains all styling logic for the Inline Container block
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import {
	BackgroundAttributes,
	generateResponsiveCSS,
	getBackgroundCSS,
	getSpacingCSS,
	getTypographyCSS,
} from '@redfox-thank-you/control-groups';
import { createAttributeManager } from '@redfox-thank-you/utils';
import { applyFilters } from '@wordpress/hooks';

import type {
	InlineContainerAttributes,
	LayoutSettings,
	ResponsiveLayoutAttributes,
} from './types';

/**
 * Generate CSS styles for the Inline Container block
 *
 * @param attributes - Block attributes containing style and settings data
 * @param blockClass - Unique CSS class for the block instance
 * @returns CSS string with all block styles
 */
export function generateInlineContainerCSS(
	attributes: InlineContainerAttributes,
	blockClass: string,
): string {
	const css: string[] = [];
	const blockSelector = `.${ blockClass } .block-editor-block-list__layout`;

	// Create attribute manager to safely access nested attributes
	const { getAttribute } = createAttributeManager(
		attributes as Record< string, unknown >,
		() => {},
	);

	// Extract style properties with defaults using getAttribute
	const blockTextStyle = getAttribute( 'style.block.text', {} );
	const blockBackgroundStyle = getAttribute(
		'style.block.background',
		{},
	) as BackgroundAttributes;
	const blockSpacingStyle = getAttribute( 'style.block.spacing', {} );

	// Extract responsive layout settings
	const responsiveLayoutSettings = getAttribute< ResponsiveLayoutAttributes >(
		'settings.layout',
		{},
	);

	// Generate responsive layout CSS using utility function
	const layoutCSS = generateResponsiveCSS(
		responsiveLayoutSettings,
		blockSelector,
		( settings: LayoutSettings ) => {
			const styles: Record< string, string > = {};

			// Always ensure display flex is set when layout settings are present
			styles.display = 'flex';

			// Add layout properties
			if ( settings.flexDirection ) {
				styles[ 'flex-direction' ] = settings.flexDirection;
			}
			if ( settings.flexWrap ) {
				styles[ 'flex-wrap' ] = settings.flexWrap;
			}
			if ( settings.justifyContent ) {
				styles[ 'justify-content' ] = settings.justifyContent;
			}
			if ( settings.alignItems ) {
				styles[ 'align-items' ] = settings.alignItems;
			}
			if ( settings.rowGap ) {
				styles[ 'row-gap' ] = settings.rowGap;
			}
			if ( settings.columnGap ) {
				styles[ 'column-gap' ] = settings.columnGap;
			}

			// Add gap shorthand for older browsers
			if ( settings.rowGap && settings.columnGap ) {
				styles.gap = `${ settings.rowGap } ${ settings.columnGap }`;
			} else if ( settings.rowGap && ! settings.columnGap ) {
				styles.gap = `${ settings.rowGap } 0`;
			} else if ( settings.columnGap && ! settings.rowGap ) {
				styles.gap = `0 ${ settings.columnGap }`;
			}

			return styles;
		},
	);

	if ( layoutCSS ) {
		css.push( layoutCSS );
	}

	// Block text styles
	const blockTextTypographyCSS = getTypographyCSS(
		blockTextStyle,
		blockSelector,
	);
	if ( blockTextTypographyCSS ) {
		css.push( blockTextTypographyCSS );
	}

	// Background styles - responsive
	const backgroundCSS = getBackgroundCSS(
		blockBackgroundStyle,
		blockSelector,
	);
	if ( backgroundCSS ) {
		css.push( backgroundCSS );
	}

	// Spacing styles - Generate for desktop, tablet, and mobile
	const spacingStyles = getSpacingCSS( blockSpacingStyle, blockSelector );
	if ( spacingStyles ) {
		css.push( spacingStyles );
	}

	// Add overlay support styles - responsive aware
	const hasOverlay = [ 'desktop', 'tablet', 'mobile' ].some(
		( breakpoint ) => {
			const breakpointKey =
				breakpoint as keyof typeof blockBackgroundStyle;
			const breakpointValues = blockBackgroundStyle[ breakpointKey ];
			return breakpointValues?.backgroundOverlay?.enabled;
		},
	);

	if ( hasOverlay ) {
		css.push( `
			${ blockSelector } {
				position: relative;
			}
			${ blockSelector } > * {
				position: relative;
				z-index: 2;
			}
		` );
	}

	/**
	 * Filter to extend block CSS generation.
	 * Pro plugins can use this to add additional CSS rules (e.g., border, box-shadow).
	 *
	 * @param {string} css - Current CSS string
	 * @param {Object} props - Props containing attributes and selector
	 * @param {Object} props.attributes - Block attributes
	 * @param {string} props.blockSelector - CSS selector for the block
	 * @param {string} props.blockName - Block name identifier
	 */
	const extendedCSS = applyFilters(
		'redfox_thank_you.block.css',
		css.join( '\n' ),
		{
			attributes,
			blockSelector,
			blockName: 'redfox-thank-you/inline-container',
		},
	) as string;

	return extendedCSS;
}
